import React from "react";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTableLang } from "../utils/tableLang";
import Spinner from "./Spinner";
import EmptyState from "./EmptyState";

export const dataGridSx = (theme) => ({
    border: "none",
    "& .MuiDataGrid-columnHeaders": {
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor:
            theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.02)"
                : "rgba(15,23,42,0.02)",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 600,
        fontSize: "0.78rem",
        color: theme.palette.text.secondary,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    "& .MuiDataGrid-cell": {
        display: "flex",
        alignItems: "center",
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
        outline: "none",
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: theme.palette.action.hover,
    },
    "& .MuiDataGrid-footerContainer": {
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-toolbarContainer": {
        p: 1.5,
        gap: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
});

/**
 * Themed DataGrid with built-in loading + empty handling.
 * Pass standard DataGrid props plus:
 *   - loading: bool
 *   - mutating: bool   → renders an overlay spinner without unmounting the grid
 *   - emptyTitle / emptyDescription / emptyIcon
 *   - height: grid container height (default 720)
 *   - showToolbar: bool (default true)
 *   - rowHeight: number (default 64)
 */
const DataTable = ({
    rows = [],
    columns,
    loading = false,
    mutating = false,
    emptyTitle,
    emptyDescription,
    emptyIcon,
    height = 720,
    rowHeight = 64,
    showToolbar = true,
    pageSize = 25,
    pageSizeOptions = [25, 50, 100],
    ...rest
}) => {
    const tableLang = useTableLang();

    if (loading) return <Spinner variant="inline" />;
    if (!rows || rows.length === 0) {
        return (
            <EmptyState
                icon={emptyIcon}
                title={emptyTitle || "Nema podataka"}
                description={emptyDescription}
            />
        );
    }

    return (
        <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
            {mutating && <Spinner variant="overlay" />}
            <Box sx={{ height, width: "100%", minWidth: 0 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    slots={showToolbar ? { toolbar: GridToolbar } : undefined}
                    initialState={{
                        pagination: { paginationModel: { page: 0, pageSize } },
                    }}
                    localeText={tableLang}
                    pageSizeOptions={pageSizeOptions}
                    disableRowSelectionOnClick
                    getRowHeight={() => rowHeight}
                    sx={dataGridSx}
                    {...rest}
                />
            </Box>
        </Box>
    );
};

export default DataTable;